// ⚠️ WARNING: DUAL CALENDAR ARCHITECTURE ⚠️
// This is the EMBEDDED calendar component used in OrganizerPage
// There is ANOTHER calendar page at: src/Pages/CalendarView.tsx
// Any changes to calendar logic MUST be applied to BOTH files!
// See: CALENDAR_ARCHITECTURE.md for details
// TODO: Refactor to single reusable component

import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, BookOpen, X, Edit2, Save } from 'lucide-react';

type CalendarItem = {
  id: number | string;
  title: string;
  dueDate?: string;
  course?: string;
  tone?: string;
  notes?: string;
  status?: string;
  description?: string;
  eventType?: string; // Add eventType
};

type CalendarViewProps = {
  items: CalendarItem[];
  onItemClick?: (item: CalendarItem) => void;
  onItemUpdate?: (id: number | string, updates: Partial<CalendarItem>) => void;
};

// Helper function to get icon based on event type
const getEventTypeIcon = (eventType?: string) => {
  switch(eventType) {
    case 'meeting': return '📹'; // Camera for meetings
    case 'reminder': return '⏰'; // Clock for reminders
    case 'event': return '📅'; // Calendar for events
    case 'assignment': return '📝'; // Book for assignments
    default: return '📝'; // Default to book
  }
};

const CalendarView: React.FC<CalendarViewProps> = ({ items, onItemClick, onItemUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<'month' | 'week'>('month');
  const [selectedItem, setSelectedItem] = useState<CalendarItem | null>(null);
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedItem, setEditedItem] = useState<CalendarItem | null>(null);

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Group items by date
  const itemsByDate = useMemo(() => {
    const grouped: Record<string, CalendarItem[]> = {};
    
    items.forEach(item => {
      if (item.dueDate) {
        // Extract date without timezone conversion
        // If dueDate is "2026-01-17T10:00", extract "2026-01-17"
        const dateStr = item.dueDate.split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);
        // Create date using local timezone - month is 0-indexed
        const date = new Date(year, month - 1, day);
        const dateKey = date.toLocaleDateString();
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(item);
      }
    });
    
    return grouped;
  }, [items]);

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getItemsForDay = (day: number) => {
    const date = new Date(year, month, day);
    const dateKey = date.toLocaleDateString();
    return itemsByDate[dateKey] || [];
  };

  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && 
           month === today.getMonth() && 
           year === today.getFullYear();
  };

  const isPastDue = (day: number) => {
    const date = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getItemColor = (item: CalendarItem) => {
    if (item.tone === 'discussion') return 'bg-blue-500/80';
    if (item.tone === 'assignment') return 'bg-amber-500/80';
    if (item.tone === 'experiential') return 'bg-emerald-500/80';
    if (item.course?.includes('💬')) return 'bg-cyan-500/80';
    if (item.course?.includes('📔')) return 'bg-purple-500/80';
    return 'bg-indigo-500/80';
  };

  const handleItemClick = (item: CalendarItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
    onItemClick?.(item);
  };

  const getItemTypeLabel = (item: CalendarItem) => {
    if (item.tone === 'discussion') return '🔵 Discussion';
    if (item.tone === 'assignment') return '🟡 Assignment';
    if (item.tone === 'experiential') return '🟢 Experiential';
    if (item.course?.includes('💬')) return '💬 Discussion Entry';
    if (item.course?.includes('📔')) return '📔 Journal Entry';
    return '📝 Item';
  };

  return (
    <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-900/30 to-orange-900/20 p-6 golden-calendar-pulse">
      {/* Golden Pulsing Animation Styles */}
      <style>{`
        @keyframes golden-pulse {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(251,191,36,0.4), 0 0 40px rgba(251,191,36,0.2), 0 0 60px rgba(251,191,36,0.1);
          }
          50% { 
            box-shadow: 0 0 30px rgba(251,191,36,0.6), 0 0 60px rgba(251,191,36,0.4), 0 0 90px rgba(251,191,36,0.2);
          }
        }
        .golden-calendar-pulse {
          animation: golden-pulse 2s ease-in-out infinite;
        }
        @keyframes flash-amber {
          0%, 100% { box-shadow: 0 0 0 rgba(251,191,36,0); }
          50% { box-shadow: 0 0 30px rgba(251,191,36,0.8), 0 0 60px rgba(251,191,36,0.5), 0 0 90px rgba(251,191,36,0.3); }
        }
        @keyframes flash-orange {
          0%, 100% { box-shadow: 0 0 0 rgba(249,115,22,0); }
          50% { box-shadow: 0 0 30px rgba(249,115,22,0.8), 0 0 60px rgba(249,115,22,0.5), 0 0 90px rgba(249,115,22,0.3); }
        }
        @keyframes flash-yellow {
          0%, 100% { box-shadow: 0 0 0 rgba(234,179,8,0); }
          50% { box-shadow: 0 0 30px rgba(234,179,8,0.8), 0 0 60px rgba(234,179,8,0.5), 0 0 90px rgba(234,179,8,0.3); }
        }
        .flash-hover-amber:hover {
          animation: flash-amber 1.5s ease-in-out infinite;
        }
        .flash-hover-orange:hover {
          animation: flash-orange 1.5s ease-in-out infinite;
        }
        .flash-hover-yellow:hover {
          animation: flash-yellow 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-amber-50">Calendar View</h3>
            <p className="text-sm text-amber-200/80">All your upcoming deadlines</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-sm font-semibold transition-all"
          >
            Today
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                view === 'month'
                  ? 'bg-amber-500/30 text-amber-100'
                  : 'bg-white/5 text-slate-300 hover:bg-white/10'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setView('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                view === 'week'
                  ? 'bg-amber-500/30 text-amber-100'
                  : 'bg-white/5 text-amber-200/60 hover:bg-white/10'
              }`}
            >
              Week
            </button>
          </div>
        </div>
      </div>

      {/* Month/Year Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={previousMonth}
          className="w-8 h-8 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 flex items-center justify-center transition-all"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h4 className="text-lg font-bold text-amber-50">
          {monthNames[month]} {year}
        </h4>
        <button
          onClick={nextMonth}
          className="w-8 h-8 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 flex items-center justify-center transition-all"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Day Names */}
        {dayNames.map(day => (
          <div
            key={day}
            className="text-center text-sm font-semibold text-amber-300/80 py-2"
          >
            {day}
          </div>
        ))}

        {/* Empty cells for days before month starts */}
        {Array.from({ length: startingDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}

        {/* Calendar Days */}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const dayItems = getItemsForDay(day);
          const today = isToday(day);
          const past = isPastDue(day);
          const hasItems = dayItems.length > 0;
          const date = new Date(year, month, day);
          const dateKey = date.toLocaleDateString();
          const isSelectedDay = selectedDayKey === dateKey;

          return (
            <div
              key={day}
              onClick={() => {
                const dateStr = date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
                setSelectedDayKey(dateKey);
                
                if (hasItems && dayItems.length > 0) {
                  // If there are existing items, show them
                  console.log('Calendar day clicked:', dateStr, 'Items:', dayItems.length);
                  if (onItemClick) {
                    onItemClick({ date: dateStr, items: dayItems, dateObj: date } as any);
                  }
                } else {
                  // Empty day - allow adding new event
                  console.log('Empty day clicked:', dateStr);
                  if (onItemClick) {
                    onItemClick({ date: dateStr, items: [], dateObj: date, isEmpty: true } as any);
                  }
                }
              }}
              className={`aspect-square rounded-lg border transition-all relative group cursor-pointer ${
                isSelectedDay
                  ? 'border-amber-300 bg-amber-500/25 ring-2 ring-amber-300/70 shadow-[0_0_24px_rgba(251,191,36,0.55),0_0_45px_rgba(251,191,36,0.3)]'
                  : today
                  ? 'border-amber-400 bg-amber-500/20'
                  : past
                  ? 'border-white/5 bg-black/20 hover:bg-black/30'
                  : 'border-amber-400/20 bg-amber-900/10 hover:bg-amber-800/20'
              } ${hasItems ? 'flash-hover-amber' : 'hover:border-amber-400/40'}`}
            >
              <div className="h-full flex flex-col p-2 relative z-10">
                <div
                  className={`text-base font-bold mb-1 ${
                    today
                      ? 'text-amber-100'
                      : past
                      ? 'text-slate-500'
                      : 'text-amber-50'
                  }`}
                >
                  {day}
                </div>
                
                {/* Items for this day */}
                <div
                  className={`flex-1 space-y-1 overflow-x-hidden pr-0.5 ${
                    dayItems.length > 2 ? 'overflow-y-auto' : 'overflow-y-hidden'
                  }`}
                >
                  {dayItems.map((item, idx) => (
                    <div
                      key={idx}
                      className={`w-full text-left px-2 py-1 rounded text-xs font-medium text-white truncate ${getItemColor(item)} flex items-center gap-1`}
                      title={item.title}
                    >
                      <span className="text-sm flex-shrink-0">{getEventTypeIcon(item.eventType)}</span>
                      <span className="truncate">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-white/10">
        <p className="text-sm text-slate-400 mb-2 font-semibold">Color Legend:</p>
        <div className="flex flex-wrap gap-3 text-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-500/80" />
            <span className="text-slate-300">Discussion</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-amber-500/80" />
            <span className="text-slate-300">Assignment</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-500/80" />
            <span className="text-slate-300">Experiential</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-cyan-500/80" />
            <span className="text-slate-300">Discussion Entry</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-purple-500/80" />
            <span className="text-slate-300">Journal Entry</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-white/5 p-3 text-center">
          <div className="text-2xl font-bold text-white">{items.length}</div>
          <div className="text-xs text-slate-400">Total Items</div>
        </div>
        <div className="rounded-lg bg-white/5 p-3 text-center">
          <div className="text-2xl font-bold text-indigo-300">
            {items.filter(i => i.dueDate && new Date(i.dueDate) > new Date()).length}
          </div>
          <div className="text-xs text-slate-400">Upcoming</div>
        </div>
        <div className="rounded-lg bg-white/5 p-3 text-center">
          <div className="text-2xl font-bold text-red-300">
            {items.filter(i => i.dueDate && new Date(i.dueDate) < new Date()).length}
          </div>
          <div className="text-xs text-slate-400">Overdue</div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-[#1a0f2e] to-[#0a0118] border-2 border-purple-400/30 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-purple-400/30">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {isEditing ? 'Edit Item' : selectedItem.title}
                  </h2>
                  <p className="text-sm text-purple-200">{getItemTypeLabel(selectedItem)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isEditing && onItemUpdate && (
                  <>
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setEditedItem({ ...selectedItem });
                      }}
                      className="w-10 h-10 rounded-full bg-amber-500/20 hover:bg-amber-500/30 flex items-center justify-center transition-colors text-amber-300"
                      title="Edit item"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to permanently delete "${selectedItem.title}"?\n\nThis action cannot be undone.`)) {
                          // Call onItemUpdate with a special delete flag
                          onItemUpdate(selectedItem.id, { _delete: true } as any);
                          setShowDetailModal(false);
                        }
                      }}
                      className="w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center transition-colors text-red-300"
                      title="Delete permanently"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setIsEditing(false);
                    setEditedItem(null);
                  }}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isEditing && editedItem ? (
                // Edit Form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Title *</label>
                    <input
                      type="text"
                      value={editedItem.title}
                      onChange={(e) => setEditedItem({ ...editedItem, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors"
                      placeholder="Item title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Due Date *</label>
                    <input
                      type="datetime-local"
                      value={editedItem.dueDate ? new Date(editedItem.dueDate).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditedItem({ ...editedItem, dueDate: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Course/Module</label>
                    <input
                      type="text"
                      value={editedItem.course || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, course: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors"
                      placeholder="Course or module name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Description</label>
                    <textarea
                      value={editedItem.description || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors resize-none"
                      placeholder="Brief description"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Notes</label>
                    <textarea
                      value={editedItem.notes || ''}
                      onChange={(e) => setEditedItem({ ...editedItem, notes: e.target.value })}
                      rows={6}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors resize-none"
                      placeholder="Additional notes or details"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-purple-200 mb-2">Status</label>
                    <select
                      value={editedItem.status || 'upcoming'}
                      onChange={(e) => setEditedItem({ ...editedItem, status: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg bg-white/5 border border-purple-400/30 text-white focus:outline-none focus:border-purple-400 transition-colors"
                    >
                      <option value="upcoming">Upcoming</option>
                      <option value="completed">Completed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="space-y-4">
                  {/* Due Date */}
                  {selectedItem.dueDate && (
                    <div className="rounded-xl bg-purple-900/20 border border-purple-400/30 p-4">
                      <div className="flex items-center gap-2 text-purple-200 mb-1">
                        <Clock className="w-4 h-4" />
                        <span className="font-semibold">Due Date</span>
                      </div>
                      <p className="text-white text-lg">
                        {new Date(selectedItem.dueDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {new Date(selectedItem.dueDate) < new Date() && (
                        <p className="text-red-300 text-sm mt-1">⚠️ This item is overdue</p>
                      )}
                    </div>
                  )}

                  {/* Course/Module */}
                  {selectedItem.course && (
                    <div className="rounded-xl bg-indigo-900/20 border border-indigo-400/30 p-4">
                      <p className="font-semibold text-indigo-200 mb-1">Course/Module</p>
                      <p className="text-white">{selectedItem.course}</p>
                    </div>
                  )}

                  {/* Description */}
                  {selectedItem.description && (
                    <div className="rounded-xl bg-emerald-900/20 border border-emerald-400/30 p-4">
                      <p className="font-semibold text-emerald-200 mb-2">Description</p>
                      <p className="text-white overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{selectedItem.description}</p>
                    </div>
                  )}

                  {/* Notes/Content */}
                  {selectedItem.notes && (
                    <div className="rounded-xl bg-slate-900/40 border border-slate-400/30 p-4">
                      <p className="font-semibold text-slate-200 mb-2">Notes</p>
                      <div className="text-white overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {selectedItem.notes}
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  {selectedItem.status && (
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 rounded-full bg-white/10 text-sm font-semibold text-white capitalize">
                        Status: {selectedItem.status}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-purple-400/30">
              {isEditing ? (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditedItem(null);
                    }}
                    className="flex-1 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-semibold transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (editedItem && onItemUpdate) {
                        onItemUpdate(selectedItem.id, editedItem);
                        setSelectedItem(editedItem);
                        setIsEditing(false);
                        setEditedItem(null);
                      }
                    }}
                    className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save Changes
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setIsEditing(false);
                    setEditedItem(null);
                  }}
                  className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold transition-all"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
